import { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { User, Calendar, Star, TrendingUp, Briefcase, Clock, ArrowLeft, Package } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

function PublicProfile() {
  const { username } = useParams();
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchPublicProfile();
  }, [username]);

  const fetchPublicProfile = async () => {
    try {
      const response = await axios.get(`${API}/users/${username}/public-profile`);
      setProfile(response.data);
    } catch (error) {
      console.error('Error fetching profile:', error);
      toast.error('Failed to load user profile');
    } finally {
      setLoading(false);
    }
  };

  const getCategoryColor = (category) => {
    const colors = {
      'Education': 'bg-blue-100 text-blue-800',
      'Technology': 'bg-purple-100 text-purple-800',
      'Creative': 'bg-pink-100 text-pink-800',
      'Health': 'bg-green-100 text-green-800',
      'Home': 'bg-orange-100 text-orange-800',
      'Other': 'bg-gray-100 text-gray-800',
    };
    return colors[category] || colors['Other'];
  };

  if (loading) {
    return <div className="text-center py-8">Loading profile...</div>;
  }

  if (!profile) {
    return (
      <div className="text-center py-12">
        <p className="text-gray-500 mb-4">User not found</p>
        <Link to="/marketplace">
          <Button variant="outline">
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Marketplace
          </Button>
        </Link>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="public-profile">
      {/* Back Button */}
      <Link to="/marketplace">
        <Button variant="ghost" size="sm">
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back to Marketplace
        </Button>
      </Link>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Profile Info Card */}
        <Card className="bg-white shadow-lg lg:col-span-1" data-testid="profile-info-card">
          <CardHeader>
            <CardTitle>User Profile</CardTitle>
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="flex flex-col items-center">
              <div className="w-24 h-24 bg-gradient-to-br from-purple-400 to-pink-400 rounded-full flex items-center justify-center mb-4">
                <User className="w-12 h-12 text-white" />
              </div>
              <h2 className="text-2xl font-bold text-gray-800" data-testid="profile-username">{profile.username}</h2>
            </div>
            
            <div className="space-y-3">
              <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
                <Calendar className="w-5 h-5 text-blue-600" />
                <div>
                  <p className="text-xs text-gray-500">Member Since</p>
                  <p className="font-semibold text-gray-800">{new Date(profile.member_since).toLocaleDateString()}</p>
                </div>
              </div>

              {profile.average_rating && (
                <div className="flex items-center gap-3 p-3 bg-yellow-50 rounded-lg">
                  <Star className="w-5 h-5 text-yellow-600 fill-yellow-600" />
                  <div>
                    <p className="text-xs text-gray-500">Average Rating</p>
                    <div className="flex items-center gap-2">
                      <p className="font-semibold text-gray-800">{profile.average_rating} / 5</p>
                      <span className="text-xs text-gray-500">({profile.rating_count} reviews)</span>
                    </div>
                  </div>
                </div>
              )}

              <div className="flex items-center gap-3 p-3 bg-green-50 rounded-lg">
                <TrendingUp className="w-5 h-5 text-green-600" />
                <div>
                  <p className="text-xs text-gray-500">Completed Transactions</p>
                  <p className="font-semibold text-gray-800">
                    {profile.completed_as_provider + profile.completed_as_requester} total
                  </p>
                  <p className="text-xs text-gray-500">
                    {profile.completed_as_provider} as provider
                  </p>
                </div>
              </div>

              <div className="flex items-center gap-3 p-3 bg-blue-50 rounded-lg">
                <Briefcase className="w-5 h-5 text-blue-600" />
                <div>
                  <p className="text-xs text-gray-500">Active Listings</p>
                  <p className="font-semibold text-gray-800">{profile.services_count}</p>
                </div>
              </div>
            </div>

            {/* Message Button */}
            <Link to={`/messages/${profile.user_id}`}>
              <Button className="w-full" variant="default">
                Send Message
              </Button>
            </Link>
          </CardContent>
        </Card>

        {/* Services List */}
        <div className="lg:col-span-2">
          <h2 className="text-2xl font-bold text-gray-800 mb-4">
            {profile.username}'s Listings
          </h2>
          
          {profile.services.length === 0 ? (
            <Card className="bg-white shadow-lg">
              <CardContent className="py-12">
                <p className="text-center text-gray-500">No active listings</p>
              </CardContent>
            </Card>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {profile.services.map((service) => (
                <Card key={service.id} className="bg-white shadow-lg" data-testid={`service-${service.id}`}>
                  {service.images && service.images.length > 0 && (
                    <div className="relative">
                      <img 
                        src={service.images[0]} 
                        alt={service.title}
                        className="w-full h-40 object-cover rounded-t-lg"
                      />
                      {service.images.length > 1 && (
                        <Badge className="absolute top-2 right-2 bg-black/60 text-white">
                          +{service.images.length - 1}
                        </Badge>
                      )}
                    </div>
                  )}
                  <CardHeader>
                    <div className="flex items-start justify-between">
                      <div className="flex gap-2">
                        <Badge className={getCategoryColor(service.category)}>{service.category}</Badge>
                        <Badge variant="outline" className="capitalize">
                          {service.type === 'service' ? (
                            <Briefcase className="w-3 h-3 mr-1" />
                          ) : (
                            <Package className="w-3 h-3 mr-1" />
                          )}
                          {service.type}
                        </Badge>
                      </div>
                      <div className="flex items-center gap-1 text-purple-600 font-semibold">
                        <Clock className="w-4 h-4" />
                        <span>{service.time_cost}h</span>
                      </div>
                    </div>
                    <CardTitle className="mt-2">{service.title}</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-gray-600 text-sm">{service.description}</p>
                    {service.detailed_description && (
                      <p className="text-gray-500 text-xs mt-2 line-clamp-2">{service.detailed_description}</p>
                    )}
                  </CardContent>
                  <CardFooter>
                    <Link to="/marketplace" className="w-full">
                      <Button className="w-full">
                        View in Marketplace
                      </Button>
                    </Link>
                  </CardFooter>
                </Card>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default PublicProfile;
